import { useEffect, useRef, useState, useCallback } from "react";
import { X, RotateCcw, SwitchCamera, Check, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

// Snap a photo straight from the device camera and hand back a JPEG data URL.
// portrait=true frames the viewfinder 9:16 (Minute Mode); otherwise 16:9 (DIY).
export default function CameraCaptureModal({ portrait = false, onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [shot, setShot] = useState(null); // captured data URL
  const [preset, setPreset] = useState("none");
  const [sat, setSat] = useState(1);

  const PRESETS = {
    none: { label: "Original", base: "" },
    bw: { label: "B&W", base: "grayscale(1) contrast(1.08)" },
    cool: { label: "Cool Vivid", base: "saturate(1.5) hue-rotate(-12deg) brightness(1.03)" },
    warm: { label: "Warm", base: "saturate(1.35) sepia(.28) brightness(1.05)" },
  };
  const filterStr = `${PRESETS[preset].base}${preset === "bw" ? "" : ` saturate(${sat})`}`.trim();

  const stop = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const start = useCallback(async (mode) => {
    stop();
    setReady(false); setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: mode } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      setReady(true);
    } catch (e) {
      setErr("We couldn't open your camera. Please allow camera access and try again.");
    }
  }, [stop]);

  useEffect(() => { start(facing); return () => stop(); }, [facing, start, stop]);

  const flip = () => setFacing((f) => (f === "environment" ? "user" : "environment"));

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const vw = v.videoWidth, vh = v.videoHeight;
    const MAX = 1280;
    const scale = Math.min(1, MAX / Math.max(vw, vh));
    const cw = Math.round(vw * scale), ch = Math.round(vh * scale);
    const c = document.createElement("canvas");
    c.width = cw; c.height = ch;
    const ctx = c.getContext("2d");
    if (facing === "user") { ctx.translate(cw, 0); ctx.scale(-1, 1); } // un-mirror selfie
    ctx.drawImage(v, 0, 0, cw, ch);
    setShot(c.toDataURL("image/jpeg", 0.9));
  };

  const usePhoto = () => {
    if (!shot) return;
    if (!filterStr) { onCapture?.(shot); toast.success("Photo added 📸"); onClose?.(); return; }
    const im = new Image();
    im.onload = () => {
      const c = document.createElement("canvas");
      c.width = im.naturalWidth; c.height = im.naturalHeight;
      const ctx = c.getContext("2d");
      ctx.filter = filterStr;
      ctx.drawImage(im, 0, 0);
      onCapture?.(c.toDataURL("image/jpeg", 0.9));
      toast.success("Photo added 📸");
      onClose?.();
    };
    im.onerror = () => { onCapture?.(shot); onClose?.(); };
    im.src = shot;
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" data-testid="camera-overlay">
      <div className="flex flex-col items-center w-full max-w-sm">
        <div className="w-full flex items-center justify-between mb-2 px-1">
          <span className="text-white font-extrabold flex items-center gap-2"><Camera size={18} /> Take a photo</span>
          <button onClick={onClose} data-testid="camera-close" className="text-white/60 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        <div className="relative w-full overflow-hidden rounded-3xl bg-black border-2 border-[#5d8a52]" style={{ aspectRatio: portrait ? "9 / 16" : "16 / 9", maxHeight: "68vh" }}>
          {!shot && <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }} data-testid="camera-video" />}
          {shot && <img src={shot} alt="captured" className="absolute inset-0 w-full h-full object-cover" style={{ filter: filterStr || "none" }} data-testid="camera-shot" />}

          {!ready && !shot && !err && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70"><Loader2 size={26} className="spin mr-2" /> Starting camera…</div>
          )}
          {err && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white/80 px-6">
              <Camera size={30} className="mb-2 text-[#8fbf82]" />
              <p className="text-sm font-semibold" data-testid="camera-error">{err}</p>
              <button onClick={() => start(facing)} className="mt-3 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-bold">Try again</button>
            </div>
          )}
        </div>

        {/* Controls */}
        {!shot ? (
          <div className="w-full flex items-center justify-between mt-5 px-6">
            <div className="w-12" />
            <button
              onClick={snap}
              disabled={!ready}
              data-testid="camera-shutter"
              className="relative w-[74px] h-[74px] rounded-full flex items-center justify-center transition-transform active:scale-95 disabled:opacity-50"
              style={{ border: "4px solid rgba(255,255,255,.95)" }}
              aria-label="Take photo"
            >
              <span className="w-[58px] h-[58px] rounded-full bg-white transition-all" />
            </button>
            <button onClick={flip} data-testid="camera-flip" className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" aria-label="Flip camera">
              <SwitchCamera size={22} />
            </button>
          </div>
        ) : (
          <div className="w-full mt-4" data-testid="camera-filters">
            <div className="flex gap-1.5">
              {Object.entries(PRESETS).map(([k, p]) => (
                <button key={k} onClick={() => setPreset(k)} data-testid={`camera-filter-${k}`}
                  className="flex-1 rounded-lg py-2 text-[11px] font-extrabold transition-colors"
                  style={preset === k ? { background: "#ffd21e", color: "#171426", border: "2px solid #ffd21e" } : { background: "rgba(255,255,255,.08)", color: "#fff", border: "2px solid rgba(255,255,255,.15)" }}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className={`flex items-center gap-2 mt-2.5 ${preset === "bw" ? "opacity-40 pointer-events-none" : ""}`}>
              <span className="text-[11px] font-bold text-white/70 w-16 shrink-0">Saturation</span>
              <input type="range" min={0.4} max={2} step={0.05} value={sat} onChange={(e) => setSat(parseFloat(e.target.value))} data-testid="camera-saturation" className="w-full accent-[#ffd21e]" />
              <span className="text-[11px] font-extrabold text-white/70 w-8 text-right tabular-nums">{sat.toFixed(1)}x</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => { setShot(null); setPreset("none"); setSat(1); }} data-testid="camera-retake" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white font-extrabold py-3 transition-colors">
                <RotateCcw size={18} /> Retake
              </button>
              <button onClick={usePhoto} data-testid="camera-use" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-extrabold py-3 transition-colors">
                <Check size={18} /> Use Photo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
